import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import TimeSelect from '../components/TimeSelect';
import AutoExpandTextarea from '../components/AutoExpandTextarea';
import { FREE_TEXT_TEMPLATE } from '../constants/reportTemplate';
import '../styles/NewReport.css';

function NewReport() {
  const [formData, setFormData] = useState({
    shift_date: new Date().toISOString().split('T')[0],
    shift_start_time: '',
    shift_end_time: '',
    free_text: FREE_TEXT_TEMPLATE,
    template_id: 1  // Always use Standard Shift Checklist
  });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [checklistResponses, setChecklistResponses] = useState([]);
  const [checklistComment, setChecklistComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadStandardChecklist();
  }, []);

  const loadStandardChecklist = async () => {
    try {
      // Always load template ID 1 (Standard Shift Checklist)
      const templateId = 1;
      const template = await api.getTemplate(token, templateId);
      setSelectedTemplate(template);
      setFormData({ ...formData, template_id: templateId });

      // Initialize checklist responses
      const responses = template.items.map(item => ({
        item_id: item.id,
        is_checked: false,
        notes: ''
      }));
      setChecklistResponses(responses);
    } catch (err) {
      console.error('Failed to load standard checklist:', err);
      setError('Kunne ikke laste sjekkliste');
    }
  };

  const handleCheckboxChange = (itemId, checked) => {
    setChecklistResponses(prev =>
      prev.map(response =>
        response.item_id === itemId
          ? { ...response, is_checked: checked }
          : response
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation: If any checklist item is unchecked, require comment
    if (checklistResponses.length > 0) {
      const hasUnchecked = checklistResponses.some(response => !response.is_checked);
      if (hasUnchecked && !checklistComment.trim()) {
        setError('Kommentar er påkrevd når et eller flere punkter ikke er krysset av');
        return;
      }
    }

    setLoading(true);

    try {
      const reportData = {
        ...formData,
        template_id: formData.template_id || null,
        checklist_responses: checklistResponses.length > 0 ? checklistResponses : null,
        checklist_comment: checklistComment || null
      };

      await api.createReport(token, reportData);
      navigate('/reports');
    } catch (err) {
      setError(err.message || 'Failed to create report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="new-report-container">
      <div className="report-header">
        <h1>Ny skiftrapport</h1>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="report-form">
        <div className="form-section">
          <h3>Skiftinformasjon</h3>

          <div className="form-group">
            <label htmlFor="shift_date">Dato (skiftet startet) *</label>
            <input
              type="date"
              id="shift_date"
              value={formData.shift_date}
              onChange={(e) => setFormData({ ...formData, shift_date: e.target.value })}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="shift_start_time">Starttid *</label>
              <TimeSelect
                id="shift_start_time"
                value={formData.shift_start_time}
                onChange={(e) => setFormData({ ...formData, shift_start_time: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="shift_end_time">Sluttid *</label>
              <TimeSelect
                id="shift_end_time"
                value={formData.shift_end_time}
                onChange={(e) => setFormData({ ...formData, shift_end_time: e.target.value })}
                required
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Fritekst rapport ("Boka") *</h3>
          <div className="form-group">
            <AutoExpandTextarea
              id="free_text"
              minRows={8}
              value={formData.free_text}
              onChange={(e) => setFormData({ ...formData, free_text: e.target.value })}
              placeholder="Skriv litt om hvordan skiftet var og dersom du har beskjeder til neste assistent"
              required
            />
          </div>
        </div>

        {selectedTemplate && selectedTemplate.items && selectedTemplate.items.length > 0 && (
          <div className="form-section">
            <div className="checklist-items">
              {selectedTemplate.items.map((item, index) => (
                <div key={item.id} className="checklist-item">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={checklistResponses[index]?.is_checked || false}
                      onChange={(e) => handleCheckboxChange(item.id, e.target.checked)}
                    />
                    <span>{item.item_text}</span>
                  </label>
                </div>
              ))}
            </div>
            <div className="form-group" style={{ marginTop: '20px' }}>
              <label htmlFor="checklist_comment">
                Kommentar {checklistResponses.some(r => !r.is_checked) && <span style={{ color: 'var(--danger-color)' }}>*</span>}
              </label>
              <AutoExpandTextarea
                id="checklist_comment"
                minRows={4}
                value={checklistComment}
                onChange={(e) => setChecklistComment(e.target.value)}
                placeholder="Skriv kommentar her (påkrevd hvis noen punkter ikke er krysset av)..."
              />
            </div>
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/reports')}
            className="btn-secondary"
          >
            Avbryt
          </button>
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? 'Lagrer...' : 'Lagre rapport'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default NewReport;
